import React, { useState } from 'react';
import { TabBar, TabType } from './components/TabBar';
import { OnboardingSprout } from './pages/OnboardingSprout';
import { OnboardingReveal } from './pages/OnboardingReveal';
import { OnboardingTechReveal } from './pages/OnboardingTechReveal';
import { OnboardingBlindMatch } from './pages/OnboardingBlindMatch';
import { OnboardingBlindMatchAI } from './pages/OnboardingBlindMatchAI';
import { OnboardingTransition } from './pages/OnboardingTransition';
import { OnboardingRole } from './pages/OnboardingRole';
import { OnboardingThemes } from './pages/OnboardingThemes';
import { OnboardingProjects } from './pages/OnboardingProjects';
import { OnboardingRecap } from './pages/OnboardingRecap';
import { PreEventHub } from './pages/PreEventHub';
import { SummitHome } from './pages/SummitHome';
import { TreeHome } from './pages/TreeHome';
import { MatchContainer } from './pages/MatchContainer';
import { ScopriContainer } from './pages/ScopriContainer';
import { ProfilePage } from './pages/ProfilePage';
import { BestProductPage } from './pages/BestProductPage';
import { AnimatePresence, motion } from 'framer-motion';
import { LanguageProvider } from './components/LanguageContext';
import { MeetingRequestsProvider } from './components/MeetingRequestsContext';
import { SkipNudgeSheet } from './components/SkipNudgeSheet';
import { CookieConsentBanner } from './components/CookieConsentBanner';
import { ProfileStateProvider, useProfileState } from './components/ProfileStateContext';
import { OnboardingProvider, useOnboarding } from './components/OnboardingContext';
import { ScanBadgeSheet } from './components/ScanBadgeSheet';
import { AvatarNavProvider, useAvatarNav } from './components/AvatarNavContext';
type Screen = 'onboarding-sprout' | 'onboarding-reveal' | 'onboarding-tech' | 'onboarding-blind' | 'onboarding-blind-ai' | 'onboarding-transition' | 'onboarding-role' | 'onboarding-themes' | 'onboarding-projects' | 'onboarding-recap' | 'pre-event-hub' | 'summit' | 'tree-home' | 'scopri' | 'connections' | 'profilo';
const ONBOARDING_SCREENS = ['onboarding-sprout', 'onboarding-reveal', 'onboarding-blind', 'onboarding-blind-ai', 'onboarding-tech', 'onboarding-transition', 'onboarding-role', 'onboarding-themes', 'onboarding-projects', 'onboarding-recap'];
function AppInner() {
  const {
    markRoleDone,
    markThemesDone,
    markProjectDone
  } = useProfileState();
  const {
    state: onboardingState,
    setProfile,
    setInterests,
    setProject,
    markSkipped,
    markCompleted
  } = useOnboarding();
  const [currentScreen, setCurrentScreen] = useState<Screen>(() => {
    if (typeof window !== 'undefined') {
      try {
        if (window.localStorage.getItem('eiis.hasAccessedApp') === 'true') {
          return 'summit';
        }
      } catch {}
    }
    return 'onboarding-sprout';
  });
  const [activeTab, setActiveTab] = useState<TabType>('summit');
  const [fadeOverlay, setFadeOverlay] = useState(false);
  const [direction, setDirection] = useState<1 | -1>(1);
  // Skip nudge state
  const [showSkipNudge, setShowSkipNudge] = useState(false);
  const [hasSeenSkipNudge, setHasSeenSkipNudge] = useState(false);
  // Scan badge sheet state
  const [scanSheetOpen, setScanSheetOpen] = useState(false);
  const navigateForward = (screen: Screen) => {
    setDirection(1);
    setCurrentScreen(screen);
  };
  const navigateBack = (screen: Screen) => {
    setDirection(-1);
    setCurrentScreen(screen);
  };
  const {
    closeProfile
  } = useAvatarNav();
  const handleTabChange = (tab: TabType) => {
    closeProfile();
    setActiveTab(tab);
    if (tab === 'summit') setCurrentScreen('summit');
    if (tab === 'albero') setCurrentScreen('tree-home');
    if (tab === 'scopri') setCurrentScreen('scopri');
    if (tab === 'connections') setCurrentScreen('connections');
    if (tab === 'profilo') setCurrentScreen('profilo');
  };
  const handleScanPress = () => {
    setScanSheetOpen(true);
  };
  const handleOnboardingComplete = () => {
    setFadeOverlay(true);
    setTimeout(() => {
      setCurrentScreen('pre-event-hub');
      setTimeout(() => {
        setFadeOverlay(false);
      }, 50);
    }, 600);
  };
  const handleEnterApp = () => {
    try {
      window.localStorage.setItem('eiis.hasAccessedApp', 'true');
    } catch {}
    setFadeOverlay(true);
    setTimeout(() => {
      setCurrentScreen('summit');
      setActiveTab('summit');
      setTimeout(() => {
        setFadeOverlay(false);
      }, 50);
    }, 400);
  };
  const handleSkipRequest = () => {
    if (hasSeenSkipNudge) {
      handleOnboardingComplete();
    } else {
      setShowSkipNudge(true);
      setHasSeenSkipNudge(true);
    }
  };
  const showTabBar = !currentScreen.startsWith('onboarding') && currentScreen !== 'pre-event-hub';
  const isOnboarding = ONBOARDING_SCREENS.includes(currentScreen);
  // Fade (dissolve) variants for onboarding transitions
  const slideVariants = {
    initial: () => ({
      opacity: 0
    }),
    animate: {
      opacity: 1,
      transition: {
        duration: 0.35,
        ease: 'easeOut'
      }
    },
    exit: () => ({
      opacity: 0,
      transition: {
        duration: 0.25,
        ease: 'easeIn'
      }
    })
  };
  const renderScreen = () => {
    switch (currentScreen) {
      case 'onboarding-sprout':
        return <OnboardingSprout onContinue={() => navigateForward('onboarding-reveal')} onSkipToApp={handleEnterApp} data-id="element-0" />;
      case 'onboarding-reveal':
        return <OnboardingReveal onContinue={() => navigateForward('onboarding-blind')} onBack={() => navigateBack('onboarding-sprout')} data-id="element-1" />;
      case 'onboarding-blind':
        return <OnboardingBlindMatch onContinue={() => navigateForward('onboarding-blind-ai')} onBack={() => navigateBack('onboarding-reveal')} data-id="element-2" />;
      case 'onboarding-blind-ai':
        return <OnboardingBlindMatchAI onContinue={() => navigateForward('onboarding-tech')} onBack={() => navigateBack('onboarding-blind')} data-id="element-3" />;
      case 'onboarding-tech':
        return <OnboardingTechReveal onContinue={() => navigateForward('onboarding-transition')} onBack={() => navigateBack('onboarding-blind-ai')} data-id="element-4" />;
      case 'onboarding-transition':
        return <OnboardingTransition onContinue={() => navigateForward('onboarding-role')} onSkip={handleSkipRequest} onBack={() => navigateBack('onboarding-blind-ai')} data-id="element-5" />;
      case 'onboarding-role':
        return <OnboardingRole onContinue={() => {
          markRoleDone();
          setProfile(true);
          navigateForward('onboarding-themes');
        }} onSkip={() => {
          markSkipped('role');
          setProfile(false);
          handleSkipRequest();
        }} onBack={() => navigateBack('onboarding-transition')} data-id="element-6" />;
      case 'onboarding-themes':
        return <OnboardingThemes onContinue={() => {
          markThemesDone();
          setInterests(true);
          navigateForward('onboarding-projects');
        }} onSkip={() => {
          markSkipped('themes');
          setInterests(false);
          handleSkipRequest();
        }} onBack={() => navigateBack('onboarding-role')} data-id="element-7" />;
      case 'onboarding-projects':
        return <OnboardingProjects onContinue={() => {
          markProjectDone();
          setProject(true);
          navigateForward('onboarding-recap');
        }} onSkip={() => {
          markSkipped('projects');
          setProject(false);
          handleSkipRequest();
        }} onBack={() => navigateBack('onboarding-themes')} data-id="element-8" />;
      case 'onboarding-recap':
        return <OnboardingRecap onContinue={() => {
          markCompleted();
          handleOnboardingComplete();
        }} onBack={() => navigateBack('onboarding-projects')} data-id="element-9" />;
      case 'pre-event-hub':
        return <PreEventHub hasProfile={onboardingState.hasProfile} hasInterests={onboardingState.hasInterests} hasProject={onboardingState.hasProject} onEnterApp={handleEnterApp} onGoToProfile={() => {
          handleEnterApp();
          setTimeout(() => handleTabChange('profilo'), 500);
        }} onGoToAddProject={() => {
          handleEnterApp();
          setTimeout(() => handleTabChange('profilo'), 500);
        }} data-id="element-10" />;
      case 'summit':
        return <SummitHome data-id="element-11" />;
      case 'tree-home':
        return <TreeHome onNavigateToMatch={() => handleTabChange('connections')} onScanPress={handleScanPress} data-id="element-12" />;
      case 'scopri':
        return <ScopriContainer data-id="element-13" />;
      case 'connections':
        return <MatchContainer onScanPress={handleScanPress} data-id="element-14" />;
      case 'profilo':
        return <BestProductPage data-id="element-15" />;
      default:
        return null;
    }
  };
  return <>
      <CookieConsentBanner data-id="element-16" />
      <div className="h-[100dvh] bg-[#0D0D0D] flex justify-center" data-id="element-17">
        <div className="w-full max-w-[480px] h-[100dvh] bg-[#0D0D0D] relative overflow-hidden flex flex-col" data-id="element-18">
          {/* Screen Content */}
          <div className={`flex-1 overflow-hidden min-h-0 relative ${showTabBar ? 'pb-[72px]' : ''}`} data-id="element-19">
            <AnimatePresence mode="popLayout" initial={false} custom={direction} data-id="element-20">
              {isOnboarding ? <motion.div key={currentScreen} custom={direction} variants={slideVariants} initial="initial" animate="animate" exit="exit" transition={{
              type: 'tween',
              ease: [0.25, 0.46, 0.45, 0.94],
              duration: 0.4
            }} className="absolute inset-0 w-full h-full" data-id="element-21">
                  {renderScreen()}
                </motion.div> : <div key={currentScreen} className="absolute inset-0 w-full h-full" data-id="element-22">
                  {renderScreen()}
                </div>}
            </AnimatePresence>

            {/* Profile Page Overlay */}
            <ProfileOverlay data-id="element-23" />
          </div>

          {/* Tab Bar */}
          {showTabBar && <TabBar activeTab={activeTab} onTabChange={handleTabChange} data-id="element-24" />}

          {/* Fade Overlay for Transition */}
          <div className="absolute inset-0 bg-[#0D0D0D] z-[100] pointer-events-none transition-opacity duration-500 ease-in-out" style={{
          opacity: fadeOverlay ? 1 : 0
        }} data-id="element-25" />

          {/* Skip Nudge Sheet */}
          <SkipNudgeSheet isVisible={showSkipNudge} onContinue={() => setShowSkipNudge(false)} onSkipConfirm={() => {
          setShowSkipNudge(false);
          handleOnboardingComplete();
        }} onDismiss={() => setShowSkipNudge(false)} data-id="element-26" />

          {/* Scan Badge Sheet — replaces the alert() placeholder */}
          <ScanBadgeSheet isOpen={scanSheetOpen} onClose={() => setScanSheetOpen(false)} data-id="element-27" />
        </div>
      </div>
    </>;
}
function ProfileOverlay() {
  const {
    showProfile,
    closeProfile
  } = useAvatarNav();
  if (!showProfile) return null;
  const handleLogout = () => {
    try {
      window.localStorage.removeItem('eiis.hasAccessedApp');
    } catch {}
    closeProfile();
    window.location.reload();
  };
  return <div className="absolute inset-0 z-40 bg-[#0D0D0D] animate-in fade-in duration-200" data-id="element-28">
      <ProfilePage onBack={closeProfile} onLogout={handleLogout} data-id="element-29" />
    </div>;
}
export function App() {
  return <LanguageProvider data-id="element-30">
      <OnboardingProvider data-id="element-31">
        <ProfileStateProvider data-id="element-32">
          <MeetingRequestsProvider data-id="element-33">
            <AvatarNavProvider data-id="element-34">
              <AppInner data-id="element-35" />
            </AvatarNavProvider>
          </MeetingRequestsProvider>
        </ProfileStateProvider>
      </OnboardingProvider>
    </LanguageProvider>;
}